import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { 
  Crown, Check, X, Loader2, Shield, Zap, BarChart3, 
  Clock, TrendingUp, AlertCircle
} from "lucide-react";
import { apiClient } from "../App";
import { toast } from "sonner";

const SubscriptionModal = ({ isOpen, onClose, onSuccess, featureName = "this feature" }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('half_yearly');
  const [isLoading, setIsLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // Track payment events
  const trackPaymentEvent = async (eventType, planId = null, planName = null, amount = null) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      await apiClient.post('/payment/track-event', {
        event_type: eventType,
        plan_id: planId,
        plan_name: planName,
        amount: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to track event:', err);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      // Track modal opened
      trackPaymentEvent('modal_opened');
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get('/subscription/plans');
      setPlans(response.data.plans);
      // Store payment info (UPI, QR code)
      setPaymentInfo({
        upi_id: response.data.upi_id,
        qr_code_url: response.data.qr_code_url,
        payment_instructions: response.data.payment_instructions
      });
      
      // Track QR viewed if QR code exists
      if (response.data.qr_code_url) {
        trackPaymentEvent('qr_viewed');
      }
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    const plan = plans.find(p => p.id === planId);
    if (plan) {
      trackPaymentEvent('plan_selected', planId, plan.name, plan.price);
    }
  };

  const handleSubscribe = async () => {
    const selectedPlanData = plans.find(p => p.id === selectedPlan);
    
    // Track the subscription attempt
    if (selectedPlanData) {
      trackPaymentEvent('subscribe_clicked', selectedPlan, selectedPlanData.name, selectedPlanData.price);
    }
    
    // Log out the user and redirect to premium page
    toast.info('Redirecting to premium subscription page...');
    
    // Clear auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    
    // Close the modal
    onClose();
    
    // Small delay to show toast, then redirect to login page with premium tab active
    setTimeout(() => {
      // Redirect to login page with premium tab query param
      window.location.href = '/?tab=premium';
    }, 500);
  };

  // For production with Razorpay:
  const handleRazorpayPayment = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Please login first');
      return;
    }

    setProcessingPayment(true);

    try {
      // Create order
      const orderResponse = await apiClient.post('/subscription/create-order',
        { plan_id: selectedPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, amount, razorpay_key, prefill } = orderResponse.data;

      // Load Razorpay script if not loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        document.body.appendChild(script);
        await new Promise(resolve => script.onload = resolve);
      }

      // Open Razorpay checkout
      const options = {
        key: razorpay_key,
        amount: amount * 100,
        currency: 'INR',
        name: 'AlphaTradeVission',
        description: `${plans.find(p => p.id === selectedPlan)?.name} Subscription`,
        order_id: order_id,
        prefill: prefill,
        theme: { color: '#06b6d4' },
        handler: async (response) => {
          // Verify payment
          try {
            const verifyResponse = await apiClient.post('/subscription/verify-payment',
              {
                order_id: order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (verifyResponse.data.success) {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              user.subscription_status = 'active';
              user.subscription_plan = selectedPlan;
              localStorage.setItem('user', JSON.stringify(user));

              toast.success('Payment successful! Welcome to Premium.');
              if (onSuccess) onSuccess();
              onClose();
            }
          } catch (err) {
            toast.error('Payment verification failed');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      toast.error('Failed to initiate payment');
    }

    setProcessingPayment(false);
  };

  if (!isOpen) return null;

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-lg bg-[#0a0e17] border border-[#1a2540] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative p-6 pb-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-b border-[#1a2540]">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-[#6b7a99] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Upgrade to Premium</h2>
              <p className="text-sm text-amber-400/80">Unlock {featureName}</p>
            </div>
          </div>
          
          <p className="text-sm text-[#6b7a99]">
            Get full access to all premium features including Tick Log, History, Scanner, and more.
          </p>
        </div>

        {/* Premium Features */}
        <div className="p-4 border-b border-[#1a2540]">
          <p className="text-xs text-[#4a5a7a] mb-3 font-medium">PREMIUM FEATURES</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { icon: BarChart3, text: "Tick Log & Grid View" },
              { icon: Clock, text: "Historical Data" },
              { icon: TrendingUp, text: "Activity Scanner" },
              { icon: Zap, text: "Real-time Alerts" },
              { icon: Shield, text: "Advanced Analytics" },
              { icon: AlertCircle, text: "Priority Support" }
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-[#a1a1aa]">
                <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                <span className="text-xs">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Info - UPI/QR */}
        {paymentInfo?.qr_code_url && (
          <div className="p-4 border-b border-[#1a2540]">
            <p className="text-xs text-[#4a5a7a] mb-3 font-medium">PAYMENT</p>
            <div className="flex items-center gap-4">
              {paymentInfo.qr_code_url && (
                <div className="flex-shrink-0 relative">
                  {/* Loading placeholder */}
                  <div className="absolute inset-0 w-32 h-32 rounded-lg border border-[#2a3a5a] bg-[#111827] flex items-center justify-center">
                    <div className="animate-pulse flex flex-col items-center">
                      <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-[10px] text-[#6b7a99] mt-2">Loading QR...</span>
                    </div>
                  </div>
                  <img 
                    src={paymentInfo.qr_code_url} 
                    alt="Payment QR Code" 
                    className="w-32 h-32 rounded-lg border border-[#2a3a5a] bg-white relative z-10"
                    loading="eager"
                    decoding="async"
                    onLoad={(e) => { 
                      e.target.style.opacity = '1';
                      e.target.previousElementSibling.style.display = 'none';
                    }}
                    onError={(e) => { 
                      e.target.style.display = 'none';
                      e.target.previousElementSibling.innerHTML = '<span class="text-[10px] text-red-400">QR Failed</span>';
                    }}
                    style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                  />
                </div>
              )}
              <div className="flex-1">
                {paymentInfo.upi_id && (
                  <div className="mb-2">
                    <p className="text-[10px] text-[#6b7a99]">UPI ID</p>
                    <p className="text-sm font-mono text-cyan-400">{paymentInfo.upi_id}</p>
                  </div>
                )}
                {paymentInfo.payment_instructions && (
                  <p className="text-xs text-[#6b7a99]">{paymentInfo.payment_instructions}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="p-4 pt-2">
          <Button
            onClick={handleSubscribe}
            disabled={processingPayment || !selectedPlan}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-4 h-14 rounded-xl shadow-lg shadow-amber-500/30 text-base"
          >
            {processingPayment ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Redirecting...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5 mr-2" />
                Go to Premium Page
              </>
            )}
          </Button>
          
          <p className="text-[10px] text-[#4a5a7a] text-center mt-3">
            You will be redirected to the premium subscription page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
